/* Overlay toggles + slider init. Mirrors the static build: one config per rail, read off data-swiper. */
/* One toggle for both panels. Escape closes, focus returns to the trigger. */
/* ---------- the flavour engine ----------
   Flavours are line-item properties, not variants: a four-pack has to hold four different
   ones inside one line item, and a variant is only ever one combination. Each count is
   mirrored into one hidden field per flavour, disabled at zero — a disabled field posts
   nothing, so the order lists only what was bought.

   Published at module scope because two surfaces run the same picker now. The product page
   runs it above the pack cards. The cart drawer runs it on the upsell card, where there is
   no <form> to post: it reads `properties()` and hands the object to /cart/add.js instead.
   The counting rules must not fork between those two, so they live here once.

   `bind` delegates off the picker itself rather than walking the pills at boot. The drawer
   replaces its whole column after every cart write, so a handler attached to a pill would
   die with it; a handler on the picker is re-attached with the picker in one call. */
window.themeFlavors = function (picker) {
  if (!picker) return null;

  function rows() {
    return Array.prototype.slice.call(picker.querySelectorAll("[data-flavor]"));
  }
  function open() {
    return rows().filter(function (r) {
      return r.getAttribute("data-out") !== "true";
    });
  }
  function countOf(row) {
    var n = parseInt(row.getAttribute("data-count"), 10);
    return isNaN(n) ? 0 : n;
  }
  function need() {
    var n = parseInt(picker.getAttribute("data-need"), 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }
  function total() {
    var sum = 0;
    rows().forEach(function (r) {
      sum += countOf(r);
    });
    return sum;
  }
  function room() {
    return need() - total();
  }
  function full() {
    return !open().length || total() === need();
  }

  /* One flavour's count and everything that has to agree with it. The stepper is hidden on
     a single-bag pack as well as at zero: with one bag there is nothing to count, so the
     pills read as a radio group instead. */
  function setCount(row, n) {
    var single = need() < 2;
    row.setAttribute("data-count", n);
    row.classList.toggle("is-on", n > 0);
    var out = row.querySelector("[data-flavor-count]");
    if (out) out.textContent = n;
    var pick = row.querySelector("[data-flavor-pick]");
    if (pick) pick.setAttribute("aria-pressed", n > 0 ? "true" : "false");
    var step = row.querySelector("[data-flavor-step-wrap]");
    if (step) step.hidden = n < 1 || single;
    var field = row.querySelector("[data-flavor-input]");
    if (field) {
      field.value = n;
      field.disabled = n < 1;
    }
  }

  function dress() {
    var want = need();
    var have = total();
    rows().forEach(function (r) {
      setCount(r, countOf(r));
    });
    var status = picker.querySelector("[data-flavor-status]");
    if (!status) return;
    /* Nothing to count on a single-bag pack, or when every flavour is out of season. */
    if (want < 2 || !open().length) {
      status.hidden = true;
      return;
    }
    var done = have === want;
    var tpl = done
      ? status.getAttribute("data-tpl-status")
      : status.getAttribute("data-tpl-hint");
    status.hidden = false;
    status.textContent = String(tpl || "")
      .replace("[n]", have)
      .replace("[total]", want);
    status.classList.toggle("is-warn", !done);
  }

  /* Re-spend the picks after the pack size changes under them. Moving Buy 4 → Buy 2 trims
     from the last flavour back, so a customer who chose two Peanut Butter and two Vanilla
     keeps the Peanut Butter rather than having the whole row wiped; moving up tops the
     first chosen flavour back to full. Only the difference moves — never the whole row. */
  function fit() {
    var want = need();
    var all = rows();
    var over = total() - want;

    for (var i = all.length - 1; i >= 0 && over > 0; i--) {
      var have = countOf(all[i]);
      if (!have) continue;
      var take = Math.min(have, over);
      setCount(all[i], have - take);
      over -= take;
    }

    var short = want - total();
    if (short > 0) {
      var free = open();
      var onto =
        free.filter(function (r) {
          return countOf(r) > 0;
        })[0] || free[0];
      if (onto) setCount(onto, countOf(onto) + short);
    }
  }

  function setNeed(n) {
    n = parseInt(n, 10);
    if (isNaN(n) || n < 1) return;
    picker.setAttribute("data-need", n);
    if (open().length) fit();
    dress();
  }

  function flash() {
    picker.classList.remove("is-full");
    void picker.offsetWidth; /* restarts the animation; without it a second click is silent */
    picker.classList.add("is-full");
  }

  /* What the drawer posts. The product page never calls this — it has a real <form>, and the
     same hidden fields ride along in its FormData untouched. */
  function properties() {
    var out = {};
    picker.querySelectorAll("[data-flavor-input]").forEach(function (f) {
      if (f.disabled) return;
      var name =
        f.getAttribute("data-flavor-name") ||
        (f.name ? f.name.replace(/^properties\[/, "").replace(/\]$/, "") : "");
      if (!name) return;
      out[name] = f.value;
    });
    return out;
  }

  /* Full property map for /cart/change.js: active flavours get their count as a string,
     and any flavour at zero is sent as "" so Shopify explicitly removes it. */
  function changeProperties() {
    var out = {};
    picker.querySelectorAll("[data-flavor-input]").forEach(function (f) {
      var name =
        f.getAttribute("data-flavor-name") ||
        (f.name ? f.name.replace(/^properties\[/, "").replace(/\]$/, "") : "");
      if (!name) return;
      var row = f.closest("[data-flavor]");
      var n = row ? countOf(row) : 0;
      out[name] = n > 0 ? String(n) : "";
    });
    return out;
  }

  /* Revert each pill to its initial rendered count (e.g. on Cancel). */
  function reset() {
    rows().forEach(function (r) {
      var init = parseInt(r.getAttribute("data-initial-count"), 10);
      setCount(r, isNaN(init) ? 0 : init);
    });
    dress();
  }

  function bind(onChange) {
    picker.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-flavor-step]");
      if (btn && picker.contains(btn)) {
        var srow = btn.closest("[data-flavor]");
        if (!srow) return;
        var step = parseInt(btn.getAttribute("data-flavor-step"), 10) || 0;
        var ceiling = countOf(srow) + room();
        var next = countOf(srow) + step;
        if (next < 0) next = 0;
        if (next > ceiling) {
          flash();
          next = ceiling;
        }
        setCount(srow, next);
        dress();
        if (onChange) onChange();
        return;
      }

      btn = e.target.closest("[data-flavor-pick]");
      if (!btn || !picker.contains(btn)) return;
      var row = btn.closest("[data-flavor]");
      if (!row) return;
      if (need() === 1) {
        /* One bag, so the pills are a radio group: picking one clears the rest. */
        rows().forEach(function (r) {
          setCount(r, 0);
        });
        setCount(row, 1);
      } else if (countOf(row) > 0) {
        return; /* already in the pack — the stepper is how you change it */
      } else if (room() > 0) {
        setCount(row, 1);
      } else {
        flash();
        return;
      }
      dress();
      if (onChange) onChange();
    });
  }

  return {
    el: picker,
    rows: rows,
    open: open,
    need: need,
    setNeed: setNeed,
    total: total,
    full: full,
    dress: dress,
    fit: fit,
    properties: properties,
    changeProperties: changeProperties,
    reset: reset,
    bind: bind,
  };
};

document.addEventListener("DOMContentLoaded", function () {
  var last = null;
  function setOpen(el, open) {
    if (!el) return;
    el.dataset.open = open ? "true" : "false";
    document.body.style.overflow = open ? "hidden" : "";
    /* Closing a video dialog has to tear the player down, not just hide it — a hidden
       iframe keeps playing, and the audio would follow the customer down the page. */
    if (!open) {
      var slot = el.querySelector("[data-video-slot]");
      if (slot) {
        slot.querySelectorAll("iframe, video").forEach(function (p) {
          p.remove();
        });
      }
    }
    /* Any trigger that points at this panel keeps its own state honest. */
    if (el.id) {
      document
        .querySelectorAll('[aria-controls="' + el.id + '"]')
        .forEach(function (t) {
          t.setAttribute("aria-expanded", open ? "true" : "false");
        });
    }
    if (open) {
      last = document.activeElement;
      var f = el.querySelector("input,a,button");
      if (f) f.focus();
    } else if (last) {
      last.focus();
      last = null;
    }
  }
  /* Published so the cart block at the foot of this file can open the drawer after an
     add — it is the same toggle, so body scroll lock and focus return stay in one place. */
  window.themeOverlay = { set: setOpen };
  document.querySelectorAll("[data-open-search]").forEach(function (b) {
    b.addEventListener("click", function () {
      setOpen(document.getElementById("search-overlay"), true);
    });
  });
  document.querySelectorAll("[data-open-cart]").forEach(function (b) {
    b.addEventListener("click", function (e) {
      var drawer = document.getElementById("cart-drawer");
      if (!drawer) return; /* no drawer on the page: let the link go to /cart */
      e.preventDefault();
      setOpen(drawer, true);
    });
  });
  /* Mobile menu. The button used to carry aria-controls with nothing behind it, so tapping
     it did nothing at all — the drawer markup and this handler are what makes it work. */
  document.querySelectorAll("[data-open-nav]").forEach(function (b) {
    b.addEventListener("click", function () {
      setOpen(document.getElementById("mobile-nav"), true);
    });
  });
  /* Video dialog. The player lives in a <template> until the button is pressed, so a closed
     film costs nothing to load. On close the frame is emptied again — that is what actually
     stops the sound, since an iframe cannot be paused from outside. The <template> is never
     consumed, so it can be re-cloned every time the dialog is opened. */
  document.querySelectorAll("[data-open-video]").forEach(function (b) {
    b.addEventListener("click", function () {
      var panel = document.getElementById(b.getAttribute("aria-controls"));
      if (!panel) return;
      var slot = panel.querySelector("[data-video-slot]");
      var tpl = slot && slot.querySelector("[data-video-src]");
      if (slot && tpl && !slot.querySelector("iframe, video")) {
        slot.appendChild(tpl.content.cloneNode(true));
      }
      setOpen(panel, true);
    });
  });

  document.querySelectorAll("[data-close]").forEach(function (b) {
    b.addEventListener("click", function () {
      setOpen(b.closest(".overlay"), false);
    });
  });
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    document
      .querySelectorAll('.overlay[data-open="true"]')
      .forEach(function (o) {
        setOpen(o, false);
      });
  });

  /* Social lightbox. One dialog per section, repopulated from the clicked card's
     data-* attributes. Everything is textContent/setAttribute — no innerHTML, which
     the security hook rejects and which would make captions an injection surface. */
  document.querySelectorAll("[data-lb-root]").forEach(function (root) {
    var section = root.closest("section");
    if (!section) return;
    var cards = Array.prototype.slice.call(
      section.querySelectorAll("[data-lb-open]"),
    );
    if (!cards.length) return;

    var img = root.querySelector("[data-lb-img]");
    var cap = root.querySelector("[data-lb-caption]");
    var date = root.querySelector("[data-lb-date]");
    var link = root.querySelector("[data-lb-link]");
    var prev = root.querySelector("[data-lb-prev]");
    var next = root.querySelector("[data-lb-next]");
    var at = 0;

    function show(i) {
      at = (i + cards.length) % cards.length; /* wraps both ways */
      var c = cards[at];
      var caption = c.getAttribute("data-caption") || "";
      if (img) {
        img.setAttribute("src", c.getAttribute("data-img") || "");
        img.setAttribute("alt", caption);
        var w = c.getAttribute("data-w"),
          h = c.getAttribute("data-h");
        if (w && h) {
          img.setAttribute("width", w);
          img.setAttribute("height", h);
        }
      }
      if (cap) cap.textContent = caption;
      if (date) date.textContent = c.getAttribute("data-date") || "";
      if (link) {
        var href = c.getAttribute("data-link");
        link.setAttribute("href", href || "#");
        link.hidden = !href;
      }
    }

    cards.forEach(function (c, i) {
      c.addEventListener("click", function () {
        show(i);
        setOpen(root, true);
      });
    });
    if (prev)
      prev.addEventListener("click", function () {
        show(at - 1);
      });
    if (next)
      next.addEventListener("click", function () {
        show(at + 1);
      });

    root.addEventListener("keydown", function (e) {
      if (root.dataset.open !== "true") return;
      if (e.key === "ArrowLeft") show(at - 1);
      if (e.key === "ArrowRight") show(at + 1);
    });
  });

  /* ============================================================
     PRODUCT PAGE
     Gallery, option swatches, pack tiers (Buy 1 / 2 / 4), flavours, quantity.

     The pack sizes are real variants, so every price on screen is a Shopify price —
     nothing is computed in JS. The variant JSON below carries prices already run
     through the `money` filter, which keeps the shop's currency format intact and
     means this file never has to know what a currency looks like.
     ============================================================ */
  document.querySelectorAll("[data-pdp]").forEach(function (root) {
    var store = root.querySelector("[data-pdp-variants]");
    var variants = [];
    if (store) {
      try {
        variants = JSON.parse(store.textContent) || [];
      } catch (e) {
        variants = [];
      }
    }

    var packIndex = parseInt(root.getAttribute("data-pack-index"), 10);
    if (isNaN(packIndex)) packIndex = -1;

    var idInput = root.querySelector("[data-pdp-id]");
    /* Lists, not single elements: the sticky bar carries a second price and a second Add to
       cart, and the two have to say the same thing as the buy box at every moment. Writing
       to `querySelectorAll` rather than to a remembered pair is what keeps that true without
       the sticky bar needing a sync routine of its own. */
    var priceEls = Array.prototype.slice.call(
      root.querySelectorAll("[data-pdp-price]"),
    );
    var compareEls = Array.prototype.slice.call(
      root.querySelectorAll("[data-pdp-compare]"),
    );
    var submits = Array.prototype.slice.call(
      root.querySelectorAll("[data-pdp-submit]"),
    );
    var saleFlags = Array.prototype.slice.call(
      root.querySelectorAll("[data-pdp-saleflag]"),
    );
    var soldLabel = root.getAttribute("data-label-soldout") || "Sold out";
    var addLabel = root.getAttribute("data-label-add") || "Add to cart";
    var tiers = Array.prototype.slice.call(
      root.querySelectorAll("[data-tier]"),
    );
    var optInputs = Array.prototype.slice.call(
      root.querySelectorAll("[data-opt-input]"),
    );
    var packCurrent = root.querySelector("[data-pack-current]");
    /* The variant the buy button is currently pointed at. Held here because the flavour
       handlers have to re-test the button without re-running the whole variant pass. */
    var chosenNow = null;
    var purchaseOptions = root.querySelector("[data-purchase-options]");
    var subscriptionChoice = root.querySelector("[data-purchase-subscription]");
    var oneTimeChoice = root.querySelector("[data-purchase-onetime]");
    var sellingPlanInput = root.querySelector("[data-purchase-plan]");

    function updatePurchase() {
      if (!purchaseOptions) return;
      var plan = chosenNow && chosenNow.subscription;
      subscriptionChoice.disabled = !plan;
      if (!plan) oneTimeChoice.checked = true;
      var subscribed = !!(plan && subscriptionChoice.checked);
      sellingPlanInput.disabled = !subscribed;
      sellingPlanInput.value = subscribed ? plan.id : "";
      root.querySelectorAll("[data-purchase-initial]").forEach(function (el) {
        el.textContent = chosenNow ? chosenNow.price : "";
      });
      root.querySelector("[data-purchase-renewal]").textContent = plan ? plan.renewal : "Unavailable";
      root.querySelector("[data-purchase-terms]").hidden = !subscribed;
      var stickyTerms = root.querySelector("[data-sticky-purchase]");
      if (stickyTerms) stickyTerms.textContent = subscribed ? "Then " + plan.renewal + " / month" : "One-time purchase";
    }
    if (purchaseOptions) purchaseOptions.addEventListener("change", updatePurchase);
    /* The first pass must not touch the image: Liquid already rendered it with a full
       srcset, and repainting would drop that for a single fixed-width src. */
    var booted = false;

    /* ---- gallery ----
       A scroll-snapping strip, not a transform carousel: the browser owns the position, so
       a swipe, a keyboard arrow, an autoplay tick and a thumbnail click are all the same
       operation — set scrollLeft — and none of them can end up disagreeing with each other
       about which image is showing. `which()` reads the answer back off the scroll position
       rather than keeping an index of its own, which is what makes an interrupted swipe
       leave everything in step. */
    var gallery = root.querySelector("[data-pdp-gallery]");
    var track = root.querySelector("[data-pdp-track]");
    var slides = Array.prototype.slice.call(
      root.querySelectorAll("[data-pdp-slide]"),
    );
    var thumbs = Array.prototype.slice.call(
      root.querySelectorAll("[data-pdp-thumb]"),
    );
    var strip = root.querySelector("[data-pdp-thumbs]");
    var shown = 0;

    /* Whichever slide has the most of itself on screen. Compared against the track's own
       left edge, so it survives the page being scrolled sideways under it. */
    function which() {
      if (!track || !slides.length) return 0;
      var mid = track.scrollLeft + track.clientWidth / 2;
      var best = 0;
      var gap = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var c = slides[i].offsetLeft + slides[i].offsetWidth / 2;
        var d = Math.abs(c - mid);
        if (d < gap) {
          gap = d;
          best = i;
        }
      }
      return best;
    }
    /* Nudge the strip only as far as it takes to bring the live thumbnail inside it — never
       centring it, which would shunt the row on every tick and make the strip look like it
       was scrolling by itself. */
    function revealThumb(i) {
      if (!strip || !thumbs[i]) return;
      var t = thumbs[i];
      var left = t.offsetLeft;
      var right = left + t.offsetWidth;
      if (left < strip.scrollLeft) {
        strip.scrollTo({ left: left, behavior: "smooth" });
      } else if (right > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({ left: right - strip.clientWidth, behavior: "smooth" });
      }
    }
    function mark(i) {
      if (i === shown) return;
      shown = i;
      thumbs.forEach(function (t, n) {
        var on = n === i;
        t.classList.toggle("is-on", on);
        t.setAttribute("aria-current", on ? "true" : "false");
      });
      revealThumb(i);
    }
    function goTo(i, smooth) {
      if (!track || !slides.length) return;
      var last = slides.length - 1;
      /* The ends wrap, so a gallery never dead-ends on its last frame — but a wrap JUMPS.
         Smooth-scrolling from the last image back to the first sweeps the full width of the
         strip and replays every image backwards on the way, which reads as the slider having
         lost its place rather than having started over. */
      var wrapped = i < 0 || i > last;
      if (i < 0) i = last;
      if (i > last) i = 0;
      track.scrollTo({
        left: slides[i].offsetLeft,
        behavior: smooth === false || wrapped ? "auto" : "smooth",
      });
      mark(i);
    }
    /* Used by the variant pass: the slide carries the same 1400px URL the variant JSON does,
       so the match is a string compare and no index has to be kept in two places. */
    function goToSrc(src) {
      if (!src) return;
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute("data-src") === src) {
          goTo(i);
          return;
        }
      }
    }

    if (track) {
      /* rAF-throttled: a scroll fires far faster than the strip needs updating, and marking
         on every event is what makes a swipe stutter on a mid-range phone. */
      var ticking = false;
      track.addEventListener(
        "scroll",
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            ticking = false;
            mark(which());
          });
        },
        { passive: true },
      );
    }
    thumbs.forEach(function (btn, i) {
      btn.addEventListener("click", function () {
        goTo(i);
      });
    });
    root.querySelectorAll("[data-pdp-arrow]").forEach(function (btn) {
      var step = parseInt(btn.getAttribute("data-pdp-arrow"), 10) || 1;
      btn.addEventListener("click", function () {
        goTo(which() + step);
      });
    });

    /* ---- autoplay ----
       Held off for anyone who has asked their device for less motion, and paused rather than
       stopped whenever it would be fighting the customer: while the pointer is over the
       gallery, while focus is inside it, while a finger is down, and while the tab or the
       gallery itself is out of sight. A slider that keeps advancing under a hand mid-swipe is
       the one thing a customer reads as broken. */
    if (
      gallery &&
      track &&
      slides.length > 1 &&
      gallery.getAttribute("data-autoplay") === "true"
    ) {
      var calm =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)");
      if (!(calm && calm.matches)) {
        var wait = parseInt(gallery.getAttribute("data-interval"), 10);
        if (isNaN(wait) || wait < 1500) wait = 5000;
        var timer = null;
        var held = 0; /* how many reasons there are to stay still, not a boolean:
                              a pointer leaving must not restart a gallery a finger is on */
        var seen = true;

        /* Function EXPRESSIONS, not declarations: these live inside an `if` block, and a
           function declaration in a block is the one piece of syntax whose scoping still
           differs between engines. */
        var stop = function () {
          if (timer) {
            clearInterval(timer);
            timer = null;
          }
        };
        var start = function () {
          stop();
          if (held > 0 || !seen || document.hidden) return;
          timer = setInterval(function () {
            goTo(which() + 1);
          }, wait);
        };
        var hold = function () {
          held++;
          stop();
        };
        var release = function () {
          held = Math.max(0, held - 1);
          start();
        };

        ["pointerenter", "focusin", "pointerdown", "touchstart"].forEach(
          function (e) {
            gallery.addEventListener(e, hold, { passive: true });
          },
        );
        [
          "pointerleave",
          "focusout",
          "pointerup",
          "pointercancel",
          "touchend",
          "touchcancel",
        ].forEach(function (e) {
          gallery.addEventListener(e, release, { passive: true });
        });
        document.addEventListener("visibilitychange", start);

        if ("IntersectionObserver" in window) {
          new IntersectionObserver(
            function (entries) {
              seen = entries[0].isIntersecting;
              start();
            },
            { threshold: 0.35 },
          ).observe(gallery);
        }
        start();
      }
    }

    /* ---- variant resolution ---- */
    function selections() {
      var sel = [];
      optInputs.forEach(function (i) {
        if (i.checked)
          sel[parseInt(i.getAttribute("data-opt-index"), 10)] = i.value;
      });
      return sel;
    }
    function findVariant(packValue) {
      var sel = selections();
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i],
          ok = true;
        for (var o = 0; o < v.options.length; o++) {
          if (o === packIndex) {
            if (packValue !== null && v.options[o] !== packValue) {
              ok = false;
              break;
            }
          } else if (sel[o] !== undefined && v.options[o] !== sel[o]) {
            ok = false;
            break;
          }
        }
        if (ok) return v;
      }
      return null;
    }

    /* ---- flavour picker ----
       The counting rules live in `window.themeFlavors` at the top of this file, because the
       cart drawer runs the same picker on its upsell card. What stays here is only what is
       specific to this page: which pack card is lit, and what that means for the buy button.

       There is ONE picker, sitting above the pack cards. How many picks it holds is copied
       off whichever card is selected, so the same row is a single choice on Buy 1 and four
       picks on Buy 4. */
    var pickerEl = root.querySelector("[data-flavors]");
    var flavors =
      pickerEl && window.themeFlavors ? window.themeFlavors(pickerEl) : null;

    function currentTier() {
      if (!tiers.length) return null;
      return (
        tiers.filter(function (t) {
          var i = t.querySelector('input[type="radio"]');
          return i && i.checked;
        })[0] || null
      );
    }

    /* The buy button answers to two things: whether Shopify has the variant, and whether
       the pack adds up. Only the first decides the wording — a full-price sold-out message
       on a pack that is merely one bag short would be a lie. */
    function gate() {
      if (!submits.length) return;
      var stocked = !!(chosenNow && chosenNow.available);
      var ok = stocked;
      if (ok && flavors) ok = flavors.full();
      submits.forEach(function (b) {
        b.disabled = !ok;
        b.textContent = stocked ? addLabel : soldLabel;
        b.classList.toggle("is-cart", stocked);
        b.classList.toggle("is-soldout", !stocked);
      });
    }

    if (flavors) flavors.bind(gate);

    function apply() {
      var chosen = null;

      if (tiers.length) {
        /* First pass: reprice every tier against the options chosen above the buy box. */
        tiers.forEach(function (t) {
          var v = findVariant(t.getAttribute("data-pack-value"));
          var input = t.querySelector('input[type="radio"]');
          var now = t.querySelector("[data-tier-price]");
          var was = t.querySelector("[data-tier-compare]");
          if (v) {
            t.setAttribute("data-variant", v.id);
            if (now) now.textContent = v.price;
            if (was) {
              was.textContent = v.compare || "";
              was.hidden = !v.compare;
            }
            if (input) input.disabled = !v.available;
            t.classList.toggle("is-out", !v.available);
          } else {
            t.removeAttribute("data-variant");
            if (input) input.disabled = true;
            t.classList.add("is-out");
          }
        });

        /* If the checked tier just went out of stock, move to the first one that is not. */
        var checked = tiers.filter(function (t) {
          var i = t.querySelector('input[type="radio"]');
          return i && i.checked && !i.disabled;
        })[0];
        if (!checked) {
          var open = tiers.filter(function (t) {
            var i = t.querySelector('input[type="radio"]');
            return i && !i.disabled;
          })[0];
          if (open) {
            open.querySelector('input[type="radio"]').checked = true;
            checked = open;
          }
        }

        /* Second pass: only the selected tier shows its bag pickers, and only its
           fields stay enabled — a disabled field posts nothing, so the cart never
           receives bag choices from a pack the customer did not buy. */
        tiers.forEach(function (t) {
          var input = t.querySelector('input[type="radio"]');
          var on = !!(input && input.checked);
          t.classList.toggle("is-on", on);
          var body = t.querySelector("[data-tier-body]");
          if (body) {
            body.hidden = !on;
            body
              .querySelectorAll("select,textarea,input")
              .forEach(function (f) {
                f.disabled = !on;
              });
          }
          if (on) chosen = findVariant(t.getAttribute("data-pack-value"));
        });
      } else {
        chosen = findVariant(null);
      }

      /* "Quantity: Buy 4" — the line over the cards follows the card that is lit. */
      var live = currentTier();
      if (packCurrent && live)
        packCurrent.textContent = live.getAttribute("data-pack-value") || "";

      /* The picker sits above the cards, so choosing a pack is what tells it how many picks
         it holds. Re-fit before dressing: the row has to add up to the NEW number before the
         counter and the buy button are asked whether it does. */
      if (flavors) {
        if (live) flavors.setNeed(live.getAttribute("data-flavor-need"));
        else
          flavors.dress(); /* no pack cards on this product: the picker still has to
                                   paint itself once, or its fields would never be dressed */
      }

      if (idInput && chosen) idInput.value = chosen.id;
      if (chosen) {
        priceEls.forEach(function (el) {
          el.textContent = chosen.price;
        });
        compareEls.forEach(function (el) {
          el.textContent = chosen.compare || "";
          el.hidden = !chosen.compare;
        });
        /* The reference colours the live figure red only while it is a discount, so the flag
           moves with the tier rather than being painted on once at render. */
        saleFlags.forEach(function (el) {
          el.classList.toggle("is-sale", !!chosen.compare);
        });
      }
      chosenNow = chosen;
      updatePurchase();
      gate();
      /* A variant with its own photo scrolls the slider to that photo rather than repainting
         one frame: the image is already in the strip, so moving to it keeps the gallery and
         the thumbnails telling the same story. */
      if (booted && chosen && chosen.image) goToSrc(chosen.image);

      /* Keep the "Size: 5lb Bag" style labels in step with the swatch that is lit. */
      optInputs.forEach(function (i) {
        if (!i.checked) return;
        var row = i.closest("[data-opt-row]");
        if (!row) return;
        var out = row.querySelector("[data-opt-current]");
        if (out) out.textContent = i.value;
      });
    }

    optInputs.forEach(function (i) {
      i.addEventListener("change", apply);
    });
    tiers.forEach(function (t) {
      var input = t.querySelector('input[type="radio"]');
      if (input) input.addEventListener("change", apply);
    });

    /* The gift-note toggle used to live here. It was removed from the buy box, so the
       handler goes with it rather than staying behind to query an element that is never
       rendered. */

    /* ---- sticky buy bar ----
       Up once the real Add to cart has left the top of the screen, down again the moment it
       comes back. Watching the buy box itself rather than a scroll offset means the bar
       answers the question a customer would ask — "can I still reach the button?" — on every
       screen width, with no number in this file that has to match a number in the stylesheet.

       `top < 0` is doing real work: without it the bar would also raise itself while the buy
       box is still BELOW the fold on the way down the page, which is the whole first screen
       of a phone. Only a buy box that has gone past counts. */
    var sticky = root.querySelector("[data-pdp-sticky]");
    var buyRow = root.querySelector(".pdp_buy");
    if (sticky && buyRow && "IntersectionObserver" in window) {
      var stickyBtn = sticky.querySelector("[data-pdp-submit]");
      var raise = function (up) {
        sticky.classList.toggle("is-up", up);
        sticky.setAttribute("aria-hidden", up ? "false" : "true");
        /* A button parked off the bottom of the window must not be a tab stop. */
        if (stickyBtn) {
          if (up) stickyBtn.removeAttribute("tabindex");
          else stickyBtn.setAttribute("tabindex", "-1");
        }
        /* Give the page back the height the bar is standing on, so it covers the end of the
           content instead of the footer. Measured at the moment it goes up rather than
           hard-coded: the bar is a line taller once a long product name wraps its title. */
        document.body.classList.toggle("has-sticky-buy", up);
        if (up) {
          document.documentElement.style.setProperty(
            "--sticky-buy-height",
            sticky.offsetHeight + "px",
          );
        }
      };
      raise(false);
      new IntersectionObserver(
        function (entries) {
          var e = entries[0];
          raise(!e.isIntersecting && e.boundingClientRect.top < 0);
        },
        { threshold: 0 },
      ).observe(buyRow);
    }

    /* ---- copy link ---- */
    root.querySelectorAll("[data-copy-link]").forEach(function (b) {
      b.addEventListener("click", function () {
        var url = b.getAttribute("data-url") || window.location.href;
        var done = b.getAttribute("data-done") || "Copied";
        var label = b.getAttribute("aria-label") || "";
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(url).then(function () {
          b.setAttribute("aria-label", done);
          b.classList.add("is-done");
          setTimeout(function () {
            b.setAttribute("aria-label", label);
            b.classList.remove("is-done");
          }, 1600);
        });
      });
    });

    apply();
    booted = true;
  });

  /* Copy a literal string — the gift card code. Separate from the share buttons, which
     copy a URL: here the label itself is swapped so the confirmation is visible, not just
     announced to a screen reader. */
  document.querySelectorAll("[data-copy-text]").forEach(function (b) {
    b.addEventListener("click", function () {
      var text = b.getAttribute("data-text") || "";
      var done = b.getAttribute("data-done") || "Copied";
      if (!text || !navigator.clipboard) return;
      var was = b.textContent;
      navigator.clipboard.writeText(text).then(function () {
        b.textContent = done;
        b.classList.add("is-done");
        setTimeout(function () {
          b.textContent = was;
          b.classList.remove("is-done");
        }, 1800);
      });
    });
  });

  document.querySelectorAll("[data-print]").forEach(function (b) {
    b.addEventListener("click", function () {
      window.print();
    });
  });

  /* ============================================================
     QUANTITY STEPPERS
     One handler for the whole theme: the product form and every cart line use the same
     .qty markup. A stepper inside [data-qty-submit] (the cart) posts the form, so the
     Update button is only ever a no-JS fallback.
     ============================================================ */
  document.querySelectorAll("[data-qty-step]").forEach(function (b) {
    b.addEventListener("click", function () {
      var box = b.closest(".qty");
      var input = box && box.querySelector("[data-qty-input]");
      if (!input) return;
      var step = parseInt(b.getAttribute("data-qty-step"), 10) || 1;
      var n = parseInt(input.value, 10);
      if (isNaN(n)) n = 1;
      var min = parseInt(input.getAttribute("min"), 10);
      if (isNaN(min)) min = 1;
      n += step;
      if (n < min) n = min;
      input.value = n;
      if (!box.hasAttribute("data-qty-submit")) return;
      var form = b.closest("form");
      if (!form) return;
      b.disabled = true; /* stops a double tap posting twice */
      if (form.requestSubmit) form.requestSubmit();
      else form.submit();
    });
  });

  /* Product card hover gallery. While the pointer is on the card the track slides one shot at
     a time; leaving slides back to the featured image, so a card at rest always shows what the
     merchant chose. Pointer-only — a touch screen has no hover to drive it, and an
     auto-playing card on a phone would just fight the shopper's scroll. The dots are built
     here rather than in Liquid so they never appear without the script that moves them. */
  var canHover =
    !window.matchMedia || window.matchMedia("(hover: hover)").matches;
  var stillPlease =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (canHover) {
    document.querySelectorAll("[data-card-gallery]").forEach(function (media) {
      var track = media.querySelector(".pcard_track");
      var shots = media.querySelectorAll(".pcard_shot");
      if (!track || shots.length < 2) return;
      var card = media.closest(".pcard") || media;
      var dots = document.createElement("div");
      dots.className = "pcard_dots";
      dots.setAttribute("aria-hidden", "true");
      shots.forEach(function (s, n) {
        var d = document.createElement("span");
        if (n === 0) d.className = "is-active";
        dots.appendChild(d);
      });
      media.appendChild(dots);

      var at = 0;
      var step = 1;
      var timer = null;
      /* The extra shots arrive src-less so a collection grid does not pay for them up front.
         First hover is what turns them into real images, and it only ever runs once. */
      function wake() {
        shots.forEach(function (s) {
          if (!s.dataset.shotSrc) return;
          s.srcset = s.dataset.shotSrcset || "";
          s.src = s.dataset.shotSrc;
          delete s.dataset.shotSrc;
        });
      }
      function show(n) {
        dots.children[at].classList.remove("is-active");
        at = n;
        dots.children[at].classList.add("is-active");
        track.style.transform = "translate3d(" + at * -100 + "%,0,0)";
      }
      /* Back and forth rather than wrapping: a wrap from the last shot to the first has to
         sweep the whole track backwards, which reads as a glitch. On a two-image product
         this is just the usual A/B swap. */
      function next() {
        if (at + step > shots.length - 1 || at + step < 0) step = -step;
        show(at + step);
      }
      card.addEventListener("mouseenter", function () {
        wake();
        step = 1;
        /* Reduced motion still gets the second shot — one move, no loop. */
        if (stillPlease) {
          show(1);
          return;
        }
        if (timer) return;
        show(1);
        timer = setInterval(next, 900);
      });
      card.addEventListener("mouseleave", function () {
        clearInterval(timer);
        timer = null;
        show(0);
      });
    });
  }

  if (typeof Swiper === "undefined") return;
  document.querySelectorAll("[data-swiper]").forEach(function (el) {
    var per;
    try {
      per = JSON.parse(el.dataset.swiper);
    } catch (e) {
      return;
    }

    var autoBreakpoints;
    if (per.auto && per.centeredMax) {
      autoBreakpoints = {};
      autoBreakpoints[per.centeredMax + 1] = { centeredSlides: false };
    }

    new Swiper(el, {
      slidesPerView: per.auto ? "auto" : per.base,
      spaceBetween: per.gap || 26,
      loop: per.loop === true,
      /* Centered slides sekarang cuma berlaku di mobile (default, sebelum breakpoint
         pertama). Begitu masuk breakpoint 560px ke atas, kita matikan lewat override
         di breakpoints, biar tablet/desktop balik ke slide rata kiri seperti biasa. */
      centeredSlides: per.centered === true,
      autoplay: per.autoplay
        ? { delay: per.autoplay, disableOnInteraction: true }
        : false,
      pagination: {
        el: el.querySelector(".swiper-pagination"),
        clickable: true,
      },
      navigation: per.arrows
        ? {
            prevEl: el.querySelector(".swiper-button-prev"),
            nextEl: el.querySelector(".swiper-button-next"),
          }
        : false,
      breakpoints: per.auto
        ? autoBreakpoints
        : {
            560: { slidesPerView: per.sm || per.base, centeredSlides: false },
            820: {
              slidesPerView: per.md || per.lg || per.base,
              centeredSlides: false,
            },
            1100: { slidesPerView: per.lg || per.base, centeredSlides: false },
          },
    });
  });
});

/* ============================================================
   CART DRAWER

   Every write goes to Shopify's cart AJAX API and then the whole drawer is re-rendered from
   `?sections=cart-drawer`. Nothing is recomputed here — not the free-shipping meter, not a
   line total, not the subtotal — because the server already knows the answer and discounts,
   subscriptions and per-line allocations are far too easy to get subtly wrong in the browser.

   Listeners are delegated off the drawer ROOT, which never gets replaced. Only
   `[data-cart-inner]` is swapped, so a handler bound to a button inside it would die on the
   first refresh.
   ============================================================ */
document.addEventListener("DOMContentLoaded", function () {
  var drawer = document.getElementById("cart-drawer");
  if (!drawer) return;

  var root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";
  var busy = false;

  function openDrawer() {
    if (window.themeOverlay) window.themeOverlay.set(drawer, true);
    else drawer.dataset.open = "true";
  }

  function post(url, body) {
    return fetch(root.replace(/\/$/, "") + url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw data;
        return data;
      });
    });
  }

  /* Pull the section's own HTML and swap the inner column. `.cdrawer_inner` is the seam:
     the panel, the scrim and the close button sit outside it and are never rebuilt. */
  function refresh() {
    return fetch(root + "?sections=cart-drawer", {
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        var html = sections["cart-drawer"];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, "text/html");
        var next = doc.querySelector("[data-cart-inner]");
        var here = drawer.querySelector("[data-cart-inner]");
        if (next && here) here.replaceWith(next);
        countUp();
        upsellInit();
        recsInit();
        cartLinesFlavorsInit();
      });
  }

  /* The bag number in the header is the one piece of cart state living outside the drawer,
     so it is copied across from the freshly rendered count rather than counted again. */
  function countUp() {
    var badge = drawer.querySelector("[data-cart-count]");
    if (!badge) return;
    var n = (badge.textContent.match(/\d+/) || ["0"])[0];
    document.querySelectorAll(".nav_bag_count").forEach(function (el) {
      el.textContent = n;
    });
  }

  function guard(start) {
    if (busy) return;
    busy = true;
    drawer.classList.add("is-busy");
    start()
      .then(refresh)
      .catch(function (err) {
        var msg = (err && (err.description || err.message)) || "";
        if (msg) window.alert(msg);
      })
      .then(function () {
        busy = false;
        drawer.classList.remove("is-busy");
      });
  }

  function setQty(line, qty) {
    guard(function () {
      return post("/cart/change.js", { line: line, quantity: qty });
    });
  }

  /* ---------- delegated clicks ---------- */
  drawer.addEventListener("click", function (e) {
    var el;

    el = e.target.closest("[data-line-step]");
    if (el) {
      var row = el.closest("[data-line]");
      var input = row && row.querySelector("[data-line-qty]");
      if (!row || !input) return;
      var n = parseInt(input.value, 10);
      if (isNaN(n)) n = 1;
      n += parseInt(el.getAttribute("data-line-step"), 10) || 0;
      if (n < 0) n = 0;
      input.value = n;
      setQty(parseInt(row.getAttribute("data-line"), 10), n);
      return;
    }

    el = e.target.closest("[data-line-remove]");
    if (el) {
      var line = el.closest("[data-line]");
      if (line) setQty(parseInt(line.getAttribute("data-line"), 10), 0);
      return;
    }

    el = e.target.closest("[data-cart-add]");
    if (el) {
      /* The upsell card carries a variant select; when it is there the select wins over the
         button's own default, which is only the first available variant. */
      var card = el.closest(".cdrawer_upsell");
      var select = card && card.querySelector("[data-upsell-select]");
      var id = (select && select.value) || el.getAttribute("data-cart-add");
      if (!id) return;

      /* A pack that needs flavours does not go without them. The button is already disabled
         while the picker is short, so this is the second lock rather than the first — but a
         click that arrives anyway (a stale button after a re-render, a keyboard event on a
         disabled control in an older engine) must not become a flavourless order. */
      var line = { id: Number(id), quantity: 1 };
      if (upsell && card && card === upsell.card) {
        if (!upsell.engine.full()) return;
        var props = upsell.engine.properties();
        if (Object.keys(props).length) line.properties = props;
      }

      guard(function () {
        return post("/cart/add.js", { items: [line] });
      });
      return;
    }

    /* The exclusions popover closes from its own cross — it is a <details>, so there is
       nothing to toggle but the open attribute. */
    el = e.target.closest(".cdrawer_excl_close");
    if (el) {
      var d = el.closest("details");
      if (d) d.open = false;
    }
  });

  /* ---------- typed quantity and the note ---------- */
  drawer.addEventListener("change", function (e) {
    var input = e.target.closest("[data-line-qty]");
    if (input) {
      var row = input.closest("[data-line]");
      var n = parseInt(input.value, 10);
      if (isNaN(n) || n < 0) n = 0;
      if (row) setQty(parseInt(row.getAttribute("data-line"), 10), n);
      return;
    }
    /* The note is saved without a re-render: redrawing the footer would collapse the
       disclosure the shopper is still typing in. */
    var note = e.target.closest("[data-cart-note]");
    if (note)
      post("/cart/update.js", { note: note.value }).catch(function () {});
  });

  /* ---------- flavours on the upsell card ----------
     The card offers a pack, and a pack carries its flavours as line-item properties rather
     than in its variant. Every other add button in this drawer posts an id and a quantity;
     this one has to post the properties too, or the farm receives an order for four bags
     with no answer to which four.

     Counting is not reimplemented here — `window.themeFlavors` at the top of this file owns
     it, and the product page runs the same object. All that is local is the wiring: the
     variant select tells the picker how many picks the chosen pack spends, and the Add
     button stays shut until the picker agrees it is full.

     Re-run after every cart write, because the drawer rebuilds its whole column and takes
     the previous picker down with it. */
  var upsell = null;

  function upsellInit() {
    upsell = null;
    var card = drawer.querySelector("[data-upsell-card]");
    if (!card) return;
    var picker = card.querySelector("[data-flavors]");
    if (!picker || !window.themeFlavors) return;

    var engine = window.themeFlavors(picker);
    if (!engine) return;
    var add = card.querySelector("[data-cart-add]");
    var select = card.querySelector("[data-upsell-select]");

    function gate() {
      if (!add) return;
      add.disabled = !engine.full();
    }

    /* "3 Flavor" and "4 Flavor" are different packs, so changing the select changes how many
       picks the row holds. The count rides on the option rather than being parsed out of its
       text here: Liquid already worked it out when the option was written, and doing it
       twice is how the two answers start to differ. */
    function syncNeed() {
      if (!select) return;
      var opt = select.options[select.selectedIndex];
      var want = opt && opt.getAttribute("data-flavor-need");
      if (want) engine.setNeed(want);
    }
    if (select)
      select.addEventListener("change", function () {
        syncNeed();
        gate();
      });

    engine.bind(gate);
    /* Read the select once on the way in as well as on change. Liquid seeds the picker from
       the variant it marked `selected`, but the browser is free to land on a different
       option — and a picker that says four while the select says three is a pack that can
       never be completed. */
    syncNeed();
    engine.dress();
    gate();
    upsell = { engine: engine, card: card, add: add, select: select };
  }

  /* ---------- the recommendations rail ----------
     A slider whenever the cart has lines — on a phone and on a desktop alike — and nothing at
     all when the cart is empty: an empty cart turns the same markup into a two-column grid of
     the first four in CSS, so instantiating there would only fight it. The split is CART
     STATE, not width, which is why there is no matchMedia here any more; `is-filled` on
     `.cdrawer_recs` is the one thing this reads, and the server puts it there.

     This cannot ride the theme's shared `[data-swiper]` init. That one runs once at
     DOMContentLoaded, and the server replaces `.cdrawer_inner` on every cart write — the
     instance it built would be holding detached nodes by the first quantity change, which is
     exactly how a slider ends up as a dead column of cards. So: an init that is safe to call
     again, called from refresh() alongside upsellInit().

     destroy(true, true) rather than a bare destroy(): the second argument strips the inline
     slide widths and the wrapper transform. Nothing on the page usually survives a re-render
     to need it, but emptying the cart from inside the drawer can leave this element in place
     while the shape under it becomes the grid, and Swiper's leftovers would sit on top of it. */
  var recs = null;

  function recsInit() {
    if (recs) {
      recs.destroy(true, true);
      recs = null;
    }
    var el = drawer.querySelector("[data-cart-recs]");
    if (!el || typeof Swiper === "undefined") return;
    /* The grid state owns itself in CSS. Reading the class rather than the cart count keeps
       one source of truth for the shape: whatever the server just rendered. */
    var box = el.closest(".cdrawer_recs");
    if (!box || !box.classList.contains("is-filled")) return;

    recs = new Swiper(el, {
      /* Under two cards, so the third is cut off mid-card rather than landing flush with the
         panel edge. The peek is what tells the shopper the row moves; a rail that ends flush
         reads as a grid that happens to be short. The reference sizes its slides at
         `calc(55.5% - 6px)` — 1.8 per view — at every width, and the panel is capped at 500px
         so there is no width where a wider card would be wrong. A little more room from
         tablet up, where the panel is at its full 500 and 2.2 still leaves a legible card. */
      slidesPerView: 1.8,
      spaceBetween: 12,
      breakpoints: { 560: { slidesPerView: 2.2 } },
      grabCursor: true,
      /* The bar under the row is the same gesture as a swipe of the cards, so both are wired
         to the same position. The element is passed as a node, not as a '.swiper-scrollbar'
         string: the drawer holds a different one after every re-render, and a selector would
         be resolved against the document rather than against this rail. */
      scrollbar: { el: el.querySelector(".swiper-scrollbar"), draggable: true },
    });
  }

  upsellInit();
  recsInit();

  /* ---------- cart line flavour pickers ----------
     Allows customers to adjust the flavours of a multi-pack or flavoured item directly
     from the cart drawer or cart page. Posts the updated properties to /cart/change.js. */
  function cartLinesFlavorsInit() {
    var drawers = document.querySelectorAll("[data-flavor-drawer]");
    drawers.forEach(function (flavorDrawer) {
      var picker = flavorDrawer.querySelector("[data-flavors]");
      if (!picker || !window.themeFlavors) return;
      var engine = window.themeFlavors(picker);
      if (!engine) return;

      var row = flavorDrawer.closest("[data-line]");
      if (!row) return;

      var toggle = row.querySelector("[data-flavor-edit-toggle]");
      var saveBtn = flavorDrawer.querySelector("[data-flavor-save]");
      var cancelBtn = flavorDrawer.querySelector("[data-flavor-cancel]");

      function gate() {
        if (saveBtn) saveBtn.disabled = !engine.full();
      }

      engine.bind(gate);
      engine.dress();
      gate();

      if (toggle && !toggle._hasFlavorClick) {
        toggle._hasFlavorClick = true;
        toggle.addEventListener("click", function (e) {
          e.preventDefault();
          var isHidden = flavorDrawer.hidden;
          flavorDrawer.hidden = !isHidden;
          toggle.setAttribute("aria-expanded", isHidden ? "true" : "false");
          if (isHidden) {
            engine.dress();
            gate();
          }
        });
      }

      if (cancelBtn && !cancelBtn._hasFlavorClick) {
        cancelBtn._hasFlavorClick = true;
        cancelBtn.addEventListener("click", function (e) {
          e.preventDefault();
          engine.reset();
          flavorDrawer.hidden = true;
          if (toggle) toggle.setAttribute("aria-expanded", "false");
        });
      }

      if (saveBtn && !saveBtn._hasFlavorClick) {
        saveBtn._hasFlavorClick = true;
        saveBtn.addEventListener("click", function (e) {
          e.preventDefault();
          if (!engine.full()) return;

          var line = parseInt(row.getAttribute("data-line"), 10);
          if (isNaN(line) || line < 1) return;

          var qtyInput =
            row.querySelector("[data-line-qty]") ||
            row.querySelector("[data-qty-input]");
          var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
          var props = engine.changeProperties();

          saveBtn.disabled = true;
          var origText = saveBtn.textContent;
          saveBtn.textContent = "Updating...";

          var inDrawer = drawer && drawer.contains(row);
          if (inDrawer) {
            guard(function () {
              return post("/cart/change.js", {
                line: line,
                quantity: qty,
                properties: props,
              });
            });
          } else {
            post("/cart/change.js", {
              line: line,
              quantity: qty,
              properties: props,
            })
              .then(function () {
                window.location.reload();
              })
              .catch(function (err) {
                saveBtn.disabled = false;
                saveBtn.textContent = origText;
                var msg = (err && (err.description || err.message)) || "";
                if (msg) window.alert(msg);
              });
          }
        });
      }
    });
  }

  cartLinesFlavorsInit();

  /* ---------- add to cart from the page ----------
     The product form posts to /cart/add for real when the script is absent; with it, the
     same fields are sent as FormData so every line-item property, the pack variant and the
     selling plan ride along untouched, and the drawer opens instead of the cart page. */
  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (!form.matches('form[action*="/cart/add"]')) return;

    /* Tombol "Buy Now" (name="checkout") harus native-submit ke Shopify,
       supaya redirect langsung ke checkout. Cuma tombol "Add to cart"
       (name="add") yang di-intercept jadi AJAX + buka drawer. */
    var submitter =
      e.submitter || form.querySelector('[type="submit"]:not([disabled])');
    if (submitter && submitter.name === "checkout") return;

    e.preventDefault();
    if (busy) return;
    busy = true;
    drawer.classList.add("is-busy");

    var submit = form.querySelector('[type="submit"]:not([disabled])');
    if (submit) submit.disabled = true;

    fetch(root.replace(/\/$/, "") + "/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: new FormData(form),
    })
      .then(function (r) {
        return r.json().then(function (d) {
          if (!r.ok) throw d;
          return d;
        });
      })
      .then(refresh)
      .then(openDrawer)
      .catch(function (err) {
        var msg = (err && (err.description || err.message)) || "";
        if (msg) window.alert(msg);
      })
      .then(function () {
        busy = false;
        drawer.classList.remove("is-busy");
        if (submit) submit.disabled = false;
      });
  });
});
